import React from 'react';

/**
 * Coffee bar hours, as venue clock faces. Days are 0 = Sunday. The same hours
 * the Project 9 menu page shows for the Full Tank tab (lib/vendorHours there),
 * and the plain-HTML copy and search-engine hours in public/index.html; change
 * all three together.
 */
export interface HoursRow {
  label: string;
  days: number[];
  open: string;
  close: string;
}

export const HOURS: HoursRow[] = [
  { label: 'Monday to Friday', days: [1, 2, 3, 4, 5], open: '07:30', close: '13:00' },
  { label: 'Saturday & Sunday', days: [0, 6], open: '09:00', close: '13:00' },
];

const VENUE_TIMEZONE = 'America/Los_Angeles';

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** "7:30 AM", "1 PM". */
export const formatClock = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  const hour = h % 12 || 12;
  return `${hour}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
};

export const formatRange = (row: HoursRow) => `${formatClock(row.open)} – ${formatClock(row.close)}`;

/** Day of week and minutes past midnight in Seattle, whatever the visitor's own timezone. */
const venueNow = (now: Date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: VENUE_TIMEZONE,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return { day, minutes: Number(get('hour')) * 60 + Number(get('minute')) };
};

const rowFor = (day: number) => HOURS.find((row) => row.days.includes(day));

export interface BarStatus {
  isOpen: boolean;
  /** "Open until 1 PM", "Opens 7:30 AM", "Opens tomorrow 9 AM". */
  label: string;
}

export const barStatus = (now: Date = new Date()): BarStatus => {
  const { day, minutes } = venueNow(now);
  const today = rowFor(day);
  if (today && minutes >= minutesOf(today.open) && minutes < minutesOf(today.close)) {
    return { isOpen: true, label: `Open until ${formatClock(today.close)}` };
  }
  if (today && minutes < minutesOf(today.open)) {
    return { isOpen: false, label: `Opens ${formatClock(today.open)}` };
  }
  const tomorrow = rowFor((day + 1) % 7);
  return { isOpen: false, label: tomorrow ? `Opens tomorrow ${formatClock(tomorrow.open)}` : 'Closed today' };
};

/** barStatus, re-read every minute so an open page flips at 1 PM on its own. */
export const useBarStatus = () => {
  const [status, setStatus] = React.useState<BarStatus>(() => barStatus());
  React.useEffect(() => {
    const timer = setInterval(() => setStatus(barStatus()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  return status;
};
